import React, { useEffect, useRef, ReactElement } from 'react';

interface OnboardingStepProps {
  onNext: (data: Record<string, any>) => void;
  data?: Record<string, any>;
  isLastStep?: boolean;
}

interface StepWrapperProps {
  children: ReactElement<OnboardingStepProps>;
  onNext: (data: Record<string, any>) => void;
}

/**
 * Componente de wrapper para os passos de onboarding.
 * Gerencia o ciclo de vida e impede atualizações em componentes desmontados.
 */
export const StepWrapper: React.FC<StepWrapperProps> = ({
  children,
  onNext,
}) => {
  // Verificar se o children é um elemento React válido
  if (!React.isValidElement(children)) {
    console.error('StepWrapper: O filho não é um elemento React válido');
    // Renderizar um fallback para não quebrar a UI
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-md">
        <p className="text-red-800 font-medium">Erro ao carregar componente</p>
        <p className="text-red-600 text-sm">Não foi possível renderizar o componente de etapa.</p>
      </div>
    );
  }
  
  // Ref para rastrear se o componente está montado
  const isMounted = useRef(true);
  
  // Função que controla a chamada segura do onNext
  const handleSafeNext = (data: Record<string, any>) => {
    // Verificar se o componente ainda está montado antes de chamar onNext
    if (isMounted.current) {
      // Criamos um cancelToken básico
      const canceled = { value: false };
      
      // A abordagem específica para produção vs. desenvolvimento
      // Em vez de setTimeout, que pode causar problemas em produção minificada,
      // usamos requestAnimationFrame, que é mais confiável para navegação entre componentes
      if (typeof window !== 'undefined') {
        window.requestAnimationFrame(() => {
          // Verificamos novamente se o componente está montado 
          // e se a operação não foi cancelada
          if (isMounted.current && !canceled.value) {
            // Use try/catch para garantir que erros não interrompam o fluxo
            try {
              onNext(data);
            } catch (error) {
              console.error('Error in StepWrapper while calling onNext:', error);
            }
          }
        });
      } else {
        // Fallback para ambientes onde window não está disponível
        if (isMounted.current && !canceled.value) {
          try {
            onNext(data);
          } catch (error) {
            console.error('Error in StepWrapper while calling onNext:', error);
          }
        }
      }
      
      // Retornamos uma função para cancelar, caso necessário
      return () => {
        canceled.value = true;
      };
    }
  };
  
  // Atualiza o ref quando o componente é desmontado
  useEffect(() => {
    // Inicializar o state do componente
    isMounted.current = true;
    
    // Cleanup function para quando o componente desmontar
    return () => {
      isMounted.current = false;
    };
  }, []);
  
  try {
    // Clone o children e passa o handler seguro
    return React.cloneElement(children, {
      // Mantenha outras props originais
      ...children.props,
      // Sobrescreva onNext com nossa versão segura
      onNext: handleSafeNext,
    });
  } catch (error) {
    console.error('StepWrapper: Erro ao clonar elemento', error);
    // Fallback em caso de erro
    return (
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
        <p className="text-yellow-800 font-medium">Erro ao processar componente</p>
        <p className="text-yellow-700 text-sm">Ocorreu um erro ao preparar o componente de etapa.</p>
      </div>
    );
  }
}; 